import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ButtonT1 } from "../../../../../../components/atoms/Button";
import { ConfirmationModal } from "../../../../../../components/organisms/Modal/confirmationModal";
import {
  useCreateFilterCriteriaMutation,
  useDeleteFilterCriteriaMutation,
  useUpdateFilterCriteriaMutation,
} from "../../../../../../store/apiSlice/newInterfaceApiSlice";
import { useDeleteShopifyStoreMutation } from "../../../../../../store/apiSlice/shopifyApiSlice";
import { setNotification } from "../../../../../../store/slice/miscSlice";
import { setCurrentViewInLocals } from "../../../../../../store/slice/newInterfaceSlice";
import { ReactComponent as ExportIcon } from "../../../../assets/svgIcon/listView/Export.svg";
import { FilterIcon } from "../../../../assets/svgIcon/listView/FilterIcon.jsx";
import { GeneralModal } from "../../../../components/GeneralModal/GeneralModal";
import { moduleForView, selectState } from "../../../utils/tableDataElements";
import { BlurModal } from "../../BlurModal/BlurModal";
import { ViewRules } from "../../ViewRules/ViewRules";
import { ExportData } from "../ExportData";

const emptyState = { completeListOfViews: [] };

export const CommonAction = ({
  orderType,
  selectedCurrentView,
  getConsignmentByRule,
  setSelectedCurrentView,
  setConsignmentsParamsLocal,
  selectedOrderId,
  setSelectedOrder,
  allocation,
  storeId,
  pricelistId,
}) => {
  const navigate = useNavigate();
  const [isOpenViewRules, setIsOpenViewRules] = useState(false);
  const [isDisconnectStoreOpen, setIsDisconnectStoreOpen] = useState(false);
  const [deleteShopifyStore, { isLoading: isDisconnectingStore }] =
    useDeleteShopifyStoreMutation();

  const [isOpenExportModal, setIsOpenExportModal] = useState(false);

  const [filterCriteriaForCurrentView, setFilterCriteriaForCurrentView] = useState(null);
  const [editPattern, setEditPattern] = useState(1);

  const initialState = useMemo(
    () => ({
      field: "",
      condition: "",
      value: "",
      criteria: null,
      index: 1,
    }),
    [],
  );
  const [dropDownValues, setDropDownValues] = useState([initialState]);

  const { completeListOfViews } = useSelector(state => {
    if (orderType === "allocation") {
      return state["newTransferOrder"]["allocation"][allocation];
    } else if (
      orderType === "inventory" ||
      orderType === "inventoryDetail" ||
      orderType === "inventoryTransactions"
    ) {
      return state.newWarehouse?.inventory[orderType];
    } else if (orderType === "receipt") {
      return state.receipt.receiptsLayout;
    } else if (orderType === "plannedOrder") {
      return state.planner.plannedOrdersLayout;
    } else if (orderType === "product") {
      return state.product.productsLayout;
    } else if (orderType === "customerTag") {
      return state.customer.customerTagLayout;
    } else if (orderType === "bill") {
      return state.invoice.billLayout;
    } else if (orderType === "saleInvoice") {
      return state.invoice.saleInvoiceLayout;
    } else if (orderType === "promo") {
      return state.promo.promoLayout;
    }
    return state[selectState[orderType]]?.allViews || emptyState;
  });

  const [createViewRule, { isLoading: createViewLoading }] = useCreateFilterCriteriaMutation();
  const [updateViewRule, { isLoading: updateViewLoading }] = useUpdateFilterCriteriaMutation();
  const [deleteViewRule, { isLoading: deleteViewLoading }] = useDeleteFilterCriteriaMutation();

  const dispatch = useDispatch();

  const openViewRules = () => {
    if (selectedCurrentView.value === "Default View") {
      dispatch(
        setNotification({
          type: "info",
          message: "Default view active. Switch to a custom view to set up a view rule!",
        }),
      );
      return;
    }
    const view = completeListOfViews.find(item => item.id === selectedCurrentView.id);
    if (view && view.filter_criteria) {
      setFilterCriteriaForCurrentView(view.filter_criteria);

      const patternArr = view.filter_criteria.pattern
        .split(" ")
        .filter(item => item === "OR" || item === "AND");
      let viewValue = view.filter_criteria.filter.map((item, ind) => {
        return {
          field: item.field.id,
          condition: item.condition.id,
          value: item.value,
          index: item.index,
          criteria: patternArr.length === 0 ? null : patternArr[ind],
        };
      });
      setDropDownValues(viewValue);
      if (patternArr.length > 0) setEditPattern(view.filter_criteria.pattern);
    } else {
      setFilterCriteriaForCurrentView(null);
      setDropDownValues([initialState]);
      setEditPattern(1);
    }
    setIsOpenViewRules(true);
  };

  const closeViewRules = () => {
    setIsOpenViewRules(false);
    setDropDownValues([initialState]);
    setEditPattern(1);
    setFilterCriteriaForCurrentView(null);
  };
  const closeExportModal = () => {
    setIsOpenExportModal(false);
  };

  const handleViewRules = async val => {
    const data = {
      filter: dropDownValues,
      pattern: editPattern,
      view: selectedCurrentView.id,
      orderType,
    };

    try {
      if (selectedCurrentView.is_view_rule_exists) {
        await updateViewRule({ data, id: filterCriteriaForCurrentView.id }).unwrap();
      } else {
        await createViewRule(data).unwrap();
        dispatch(
          setCurrentViewInLocals({
            view: { ...selectedCurrentView, is_view_rule_exists: true },
            module: moduleForView[orderType],
            allocation,
          }),
        );
      }

      closeViewRules();
      getConsignmentByRule();
      setSelectedCurrentView({ ...selectedCurrentView, is_view_rule_exists: true });
    } catch (_) {
      // handle error in newInterfaceApiSlice for createViewRule and updateViewRule
    }
  };
  const resetViewRules = async () => {
    if (filterCriteriaForCurrentView) {
      try {
        await deleteViewRule(filterCriteriaForCurrentView.id).unwrap();

        if (
          [
            "inventory",
            "inventoryDetail",
            "inventoryTransactions",
            "receipt",
            "product",
            "bill",
            "saleInvoice",
          ].includes(orderType)
        ) {
          setConsignmentsParamsLocal(prevState => {
            const { view_id, ...rest } = prevState; // view_id is excluded
            return {
              ...rest, // keep everything else
              offset: 0, // reset offset
            };
          });
          getConsignmentByRule();
        }
        dispatch(
          setCurrentViewInLocals({
            view: { ...selectedCurrentView, is_view_rule_exists: false },
            module: moduleForView[orderType],
            allocation,
          }),
        );
        setFilterCriteriaForCurrentView(null);
        setSelectedCurrentView({ ...selectedCurrentView, is_view_rule_exists: false });

        closeViewRules();
      } catch (error) {}
    }
  };

  return (
    <>
      {isOpenExportModal && (
        <ExportData
          selectedRowsFromList={selectedOrderId}
          closeExportData={closeExportModal}
          orderType={orderType}
          allocation={allocation}
          setSelectedOrder={setSelectedOrder}
          idOfCurrentView={selectedCurrentView.id}
        />
      )}
      {isOpenViewRules && (
        <BlurModal>
          <GeneralModal
            closeModal={closeViewRules}
            titleOfModal="View Rules"
            widthOfGeneralModal="75vw"
          >
            <ViewRules
              dropDownValues={dropDownValues}
              setDropDownValues={setDropDownValues}
              initialState={initialState}
              editPattern={editPattern}
              setEditPattern={setEditPattern}
              orderType={orderType}
              allocation={allocation}
            />

            <div className="buttonGroup">
              <ButtonT1
                title="Apply"
                onClickCallback={handleViewRules}
                loading={createViewLoading || updateViewLoading}
                disabled={
                  !(
                    dropDownValues[0].field &&
                    dropDownValues[0].condition &&
                    dropDownValues[0].value
                  )
                }
              />
              <ButtonT1
                title="Reset"
                onClickCallback={resetViewRules}
                $variant="danger"
                loading={deleteViewLoading}
                disabled={!Boolean(filterCriteriaForCurrentView)}
              />
            </div>
          </GeneralModal>
        </BlurModal>
      )}
      {![
        "product",
        "productCategory",
        "productTag",
        "warehouse",
        "productAlias",
        "supplier",
        "pack",
        "warehouseCostingRange",
        "stockZone",
        "stockLocation",
        "forecast",
        "warehouseCosting",
        "productPlan",
        "transactionReason",
        "standardCost",
        "chartsOfAccount",
        "paymentTerm",
        "billChargeType",
        "journalLine",
        "taxRate",
        "site",
        "workCenter",
        "people",
        "users",
        "boms",
        "taxCategory",
        "syncHistory",
        "shopifyStores",
        "shopifyCatalogue",
        "shopifyLinkedProducts",
        "supplierCreditNote",
        "customerCreditNote",
        "receiptAdjustment",
        "prePayment",
        "stockReplenishment",
        "plannedOrder",
        "customer",
        "customerTag",
        "customerSegment",
        "productTaxClassification",
        "unitOfMeasure",
        "equipmentType",
        "equipment",
        "picklist",
        "salesRep",
        "pricelist",
        "promo",
        "quote",
        "salesOrder",
        "stockPack",
        "delivery",
        "shipmentV1",
        "asn",
        "shipmentLeg",
        "customerReturn",
        "saleInvoice",
        "manufacturingOrder",
        "coManufacturingOrder",
      ].includes(orderType) &&
        !(
          orderType === "allocation" && ["salesOrder", "manufacturingOrder"].includes(allocation)
        ) && (
          <li
            className={`tableButton ${selectedCurrentView.is_view_rule_exists ? "isAppliedRule" : "activeState"}`}
            onClick={openViewRules}
          >
            Rules{" "}
            <FilterIcon
              color={
                selectedCurrentView.is_view_rule_exists
                  ? "var(--Green-500, #14804a) !important"
                  : "#676879"
              }
            />{" "}
          </li>
        )}

      {!["shopifyCatalogue", "shopifyLinkedProducts", "syncHistory"].includes(orderType) && (
        <li className="tableButton activeState" onClick={() => setIsOpenExportModal(true)}>
          Export <ExportIcon />
        </li>
      )}
      {orderType === "shopifyStores" && (
        <li
          className="tableButton activeState"
          onClick={() => navigate("/integration/shopify/connect")}
        >
          Create New Store
        </li>
      )}
      {orderType === "shopifyStores" && selectedOrderId?.length > 0 && (
        <li
          className="tableButton activeState"
          onClick={() => setIsDisconnectStoreOpen(true)}
        >
          Disconnect Store
        </li>
      )}
      {isDisconnectStoreOpen && (
        <ConfirmationModal
          closeModal={() => setIsDisconnectStoreOpen(false)}
          handleModalWork={async () => {
            if (isDisconnectingStore) return;
            try {
              await Promise.all(
                (selectedOrderId || []).map(id => deleteShopifyStore(id).unwrap()),
              );
              dispatch(
                setNotification({
                  type: "success",
                  message:
                    selectedOrderId.length > 1
                      ? "Shopify stores disconnected."
                      : "Shopify store disconnected.",
                }),
              );
              setSelectedOrder?.([]);
              setIsDisconnectStoreOpen(false);
              getConsignmentByRule?.();
            } catch (err) {
              dispatch(
                setNotification({
                  type: "error",
                  message: err?.data?.detail || "Could not disconnect store.",
                }),
              );
            }
          }}
          description="This soft-deletes the store: it is hidden and tokens are cleared, but product links and mappings are kept so reconnect can reuse them. Sync and order import stop immediately."
          btnName={isDisconnectingStore ? "Disconnecting…" : "Disconnect"}
          titleOfModal="Disconnect Shopify Store"
          labelForConfirmCheck="I understand sync will stop until this store is reconnected."
        />
      )}
      {orderType === "shopifyCatalogue" && (
        <li
          className="tableButton activeState"
          onClick={() => {
            if (pricelistId) {
              navigate(`/sales/pricelist/update/${pricelistId}`);
              return;
            }
            if (storeId) {
              navigate(`/integration/shopify/${storeId}/settings`);
            }
          }}
        >
          Add Products
        </li>
      )}
    </>
  );
};
